import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import type { CalculationProductWithPrices } from "../../calculations/calculationVersionEdit/useProductsWithPrices";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import { Price } from "../../misc/price";
import type { CalculationDraft } from "../CalculationDraft";

function computeTotal(productsWithPrices: CalculationProductWithPrices[]): Price {
  return productsWithPrices.reduce((sum, p) => sum.add(p.price), new Price(0));
}

export function CalculationSummary({
  value,
  onSubmit,
  productsWithPrices,
  isReadOnly,
  saveDisabled,
}: {
  value: CalculationDraft;
  onSubmit: () => Promise<void>;
  productsWithPrices: CalculationProductWithPrices[];
  isReadOnly: boolean;
  saveDisabled: boolean;
}) {
  const { currency } = value;
  const participantsNumber = {
    numberOfPaid: value.basicData.peopleCount,
    numberOfStaff: value.basicData.freePeopleCount,
  };
  const total = computeTotal(productsWithPrices);
  const totalNumeric = typeof total.value === "number" ? total.value : null;
  const currencyLabel = currency ?? "";

  const totalDisplay = currency ? total.round(2).toDisplayValue(currency) : "-";

  const perPersonDisplay =
    totalNumeric !== null && participantsNumber.numberOfPaid > 0
      ? `${Math.round(totalNumeric / participantsNumber.numberOfPaid)} ${currencyLabel}`
      : "-";

  const totalPeople = participantsNumber.numberOfPaid + participantsNumber.numberOfStaff;

  return (
    <Box sx={{ position: "sticky", top: { xs: 80, md: 240 } }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)", bgcolor: "#f8f9fa" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            mb: 3,
          }}
        >
          Podsumowanie
        </Typography>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Całkowita liczba osób:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
              }}
            >
              {totalPeople}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Osób płatnych:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
              }}
            >
              {participantsNumber.numberOfPaid}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Osób obsługi:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
              }}
            >
              {participantsNumber.numberOfStaff}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Liczba usług:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
              }}
            >
              {productsWithPrices.length}
            </Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
              }}
            >
              Koszt:
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
              }}
            >
              {totalDisplay}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Koszt / os. płatna:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
              }}
            >
              {perPersonDisplay}
            </Typography>
          </Box>
        </Stack>
        <AsyncLoadingButton
          disabled={isReadOnly || saveDisabled}
          onClick={onSubmit}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disableElevation
          startIcon={<ShoppingCartIcon />}
          sx={{ py: 1.5, borderRadius: 1, textTransform: "none" }}
        >
          Wygeneruj kalkulację
        </AsyncLoadingButton>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            mt: 2,
            display: "block",
            lineHeight: 1.6,
          }}
        >
          Poniższa kalkulacja ma charakter szacunkowy i nie stanowi oferty. Dostępność terminu oraz ostateczną cenę
          potwierdzamy po dokonaniu rezerwacji.
        </Typography>
      </Paper>
    </Box>
  );
}
